import { Collection } from '@vfs-digital-channels/ns-utils';
import { IdentityTypeDirectors, PaymentMethodCode } from 'src/features/common';
import { CartItem } from 'src/features/common/types/application/cart';
import { JOURNEY_NAME, VISITOR_CUSTOMER_TYPE } from 'src/features/common/types/tealium-service';

export const productNameMap = new Map<string, string>([
  ['VodaPay Kwika', 'kwika'],
  ['VodaPay Max', 'max'],
  ['VodaPay Max 2', 'max 2'], // not available yet
  ['VodaPay Payment Gateway', 'ecommerce'],
  ['VodaPay Tap on Phone', 'tap on phone'],
  ['VodaPay Payment Request', 'payment request'],
  ['VodaPay Chop-Chop', 'chop chop'], // not available yet
]);

export const getVisitorCustomerType = (
  isAgent: boolean,
  isBroker: boolean,
): VISITOR_CUSTOMER_TYPE => {
  if (isAgent) return VISITOR_CUSTOMER_TYPE.AGENT;

  if (isBroker) return VISITOR_CUSTOMER_TYPE.BROKER;

  return VISITOR_CUSTOMER_TYPE.CONSUMER;
};

export const getJourneyName = (
  isAgent: boolean,
  isBroker: boolean,
  isDeviceActivation: boolean,
  isContinueApplication: boolean,
  isTapOnPhone: boolean,
  isPendingApplication: boolean,
): JOURNEY_NAME => {
  switch (true) {
    case isContinueApplication:
      return JOURNEY_NAME.CONTINUE_APPLICATION;
    case isPendingApplication:
      return JOURNEY_NAME.PENDING_APPLICATION;
    case isDeviceActivation:
      return JOURNEY_NAME.DEVICE_ACTIVATION;
    case isAgent || isBroker:
      return JOURNEY_NAME.THIRD_PARTY;
    case isTapOnPhone:
      return JOURNEY_NAME.TAP_ON_PHONE;
    default:
      return JOURNEY_NAME.BUY_POS;
  }
};

export const getBuyOption = (cart?: CartItem[]): string => {
  const paymentMethods = cart?.map((cartItem) => cartItem.paymentMethodCode);
  const everythingIsRental = paymentMethods?.every(
    (paymentMethod) => paymentMethod === PaymentMethodCode.RENTAL,
  );
  const someAreRental = paymentMethods?.some(
    (paymentMethod) => paymentMethod === PaymentMethodCode.RENTAL,
  );

  if (everythingIsRental) return 'rent';
  if (someAreRental) return 'buy | rent';

  return 'buy';
};

export const getProductName = (productName: string): string => {
  return productNameMap.get(productName) || '';
};

export const getProductNamesInCart = (cart: CartItem[]): string => {
  const products = cart.filter((cartItem) => !cartItem.isTenderType);
  const product_names = products.map((product) => productNameMap.get(product.title));

  return product_names.join(' | ');
};

export const getTaggingDeliveryOptions = (cart: CartItem[], collections: Collection[]): string => {
  const isDelivery = cart.length > 0 && collections.length === 0;
  const isDeliveryAndCollection =
    cart.length !== collections.length && cart.length > 0 && collections.length > 0;

  if (isDelivery) return 'deliver later';
  if (isDeliveryAndCollection) return 'grab it now | deliver later';

  return 'grab it now';
};

// return tender types object as an array of strings with this format:
// e.g. ['payment request: [visa/ mastercard]']
export const getTaggingTenderTypes = (taggingTenderTypes: Record<string, CartItem[]>): string[] => {
  const keys = Object.keys(taggingTenderTypes);
  let result: string[] = [];

  for (let i = 0; i < keys.length; i++) {
    const titles = taggingTenderTypes[keys[i]].map((cartItem: CartItem) => cartItem.title);

    result.push(`${keys[i]}: [${titles.join(' | ')}]`);
  }

  return result;
};

export const getCitizenshipType = (identityType: IdentityTypeDirectors | undefined) => {
  const citizenTypeMap = new Map<IdentityTypeDirectors, string>([
    [IdentityTypeDirectors.RSA_ID, 'south african id'],
    [IdentityTypeDirectors.PASSPORT, 'passport'],
    [IdentityTypeDirectors.ASYLUM_SEEKER, 'asylum seeker id'],
    [IdentityTypeDirectors.REFUGEE, 'refugee id'],
  ]);

  return identityType && citizenTypeMap.get(identityType);
};
